import { CampaignData, CampaignMainInfoUpdateData, CampaignPurchase, CampaignRawData, CampaignStatus } from 'common/types/models/campaign.ts';
import { SupabaseClient, wrapSupabase } from '../types/client.ts';
import { DeferredGetter, PromiseCache } from '@zajno/common/structures/promiseCache';
import { assert } from 'common/assert.ts';
import { PurchaseRawData } from 'common/types/models/purchase.ts';
import { ProductData } from 'common/types/models/product.ts';
import { CampaignAsset } from 'common/types/dtos/updateCampaignAsset.ts';

type CampaignRelationTypes = 'product' | 'purchase' | 'user';
type CampaignsFilters = {
    id?: string;
    status?: CampaignStatus;
    filterByStatuses?: CampaignStatus[];
    userId?: string;
    email?: string;
    productId?: string;
    purchaseId?: string;
    orderNumber?: string;
    ascending?: boolean;

    pages?: {
        offset?: number;
        limit?: number;
    };

    relations?: Partial<Record<CampaignRelationTypes, boolean>>;
};

export class CampaignsRepository {

    private _cacheById: PromiseCache<CampaignData | null | undefined> | null = null;

    constructor(protected readonly client: SupabaseClient) { }

    public useCaching(use: boolean) {
        this._cacheById = use
            ? new this.PromiseCacheClass(id => this._getCampaign(id))
            : null;
        return this;
    }

    protected get PromiseCacheClass() { return PromiseCache; }

    private _getCampaign(id: string): Promise<CampaignData | null | undefined> {
        return wrapSupabase(
            this.client
                .from('campaigns')
                .select()
                .eq('id', id)
                .maybeSingle(),
        );
    }

    public async getCampaigns<T extends CampaignsFilters>(args?: T): Promise<CampaignData[]> {
        let selectFilter = '*';
        if (args?.relations?.product) {
            selectFilter += ', productRef:products(*)';
        }
        if (args?.relations?.purchase) {
            selectFilter += ', purchaseRef:purchases(*)';
        }
        if (args?.relations?.user) {
            selectFilter += ', user:users(*)';
        }

        let q = this.client
            .from('campaigns')
            .select<'*', CampaignData>(selectFilter as '*')
            .order('created_at', { ascending: !!args?.ascending });

        if (args?.id) {
            q = q.eq('id', args.id);
        }
        if (args?.filterByStatuses?.length) {
            q = q.in('status', args.filterByStatuses);
        }
        if (args?.userId) {
            q = q.eq('user_id', args.userId);
        }
        if (args?.email) {
            q = q.eq('email', args.email);
        }
        if (args?.productId) {
            q = q.eq('product_id', args.productId);
        }
        if (args?.purchaseId) {
            q = q.eq('purchase_id', args.purchaseId);
        }
        if (args?.orderNumber) {
            // add % to change it to include
            q = q.like('search_column', `${args.orderNumber}`);
        }

        if (args?.pages?.offset != null) {
            q = q.range(args.pages.offset, args.pages.offset + ((Math.max(1, args.pages.limit ?? 100)) - 1));
        } else if (args?.pages?.limit) {
            q = q.limit(args.pages.limit);
        }

        const results = await wrapSupabase(q);

        results?.forEach(r => {
            this._cacheById?.updateValueDirectly(r.id, r);
        });

        return results;
    }

    public getCampaignDeferred(id: string, allowFetch = true) {
        assert(!!this._cacheById, 'CampaignRepository.getCampaignDeferred: caching is not enabled');
        assert(!!id, 'CampaignRepository.getCampaignDeferred: id is required');

        if (!allowFetch) {
            const item = this._cacheById.getCurrent(id, false);
            return {
                current: item,
                busy: false,
                get promise() { return Promise.resolve(item); },
            } satisfies DeferredGetter<CampaignData>;
        }

        return this._cacheById.getDeferred(id);
    }

    public async getCampaign(id: string, allowCache = true): Promise<CampaignData | null | undefined> {
        assert(!!id, 'CampaignRepository.getCampaign: id is required');

        if (!allowCache || !this._cacheById) {
            return this._getCampaign(id);
        }

        const campaign = await this._cacheById.get(id);

        return campaign;
    }

    public async updateCampaign(item: CampaignData) {
        if (this._cacheById) {
            this._cacheById.updateValueDirectly(item.id, item);
        }
    }
}

type CreateCampaignData = {
    purchase: PurchaseRawData;
    product: ProductData;
    status?: CampaignStatus;
};

export class ServerCampaignsRepository extends CampaignsRepository {

    public async createCampaign(data: CreateCampaignData): Promise<CampaignRawData | null | undefined> {
        const purchaseCopy = structuredClone(data.purchase);

        if (Object.prototype.hasOwnProperty.call(purchaseCopy, 'stripe')) {
            delete purchaseCopy.stripe;
        }

        const result = await wrapSupabase(
            this.client.from('campaigns')
                .insert({
                    purchase_id: data.purchase.id,
                    product_id: data.product.id,
                    status: data.status || CampaignStatus.Pending,
                    user_id: data.purchase.user_id || null,
                    email: data.purchase.email,
                    product: data.product,
                    purchase: purchaseCopy,
                })
                .select<'*', CampaignRawData>()
                .single(),
        );

        return result;
    }

    public async updateCampaignStatus(campaignId: string, status: CampaignStatus, withRelations: boolean = true) {
        assert(!!campaignId, 'CampaignRepository.updateCampaignStatus: campaignId is required');
        assert(status != null, 'CampaignRepository.updateCampaignStatus: status is required');

        const result = await wrapSupabase(
            this.client
                .from('campaigns')
                .update({ status })
                .eq('id', campaignId)
                .select<'*', CampaignData>()
                .single<CampaignData>(),
        );

        if (!result) {
            throw new Error('CampaignRepository.updateCampaignStatus: failed to update campaign status, no result returned.');
        }

        const updatedCampaigns = await this.getCampaigns({
            id: result.id,
            relations: withRelations ? { product: true, user: true } : undefined,
        });

        if (!updatedCampaigns || updatedCampaigns.length === 0) {
            throw new Error('CampaignRepository.updateCampaignStatus: failed to update campaign status, no campaign found.');
        }

        return updatedCampaigns[0];
    }

    public async updateCampaignPlaylists(campaignId: string, playlists: string[]) {
        assert(!!campaignId, 'CampaignRepository.updateCampaignPlaylists: campaignId is required');

        const result = await wrapSupabase(
            this.client
                .from('campaigns')
                .update({ playlists: { links: playlists } })
                .eq('id', campaignId)
                .select<'*', CampaignData>()
                .single<CampaignData>(),
        );

        if (!result) {
            throw new Error('CampaignRepository.updateCampaignPlaylists: failed to update campaign playlists, no result returned.');
        }

        return result;
    }

    public async updateCampaignAsset(campaignId: string, input: CampaignAsset) {
        assert(!!input, 'CampaignRepository.updateCampaignPurchaseInputData: input is required');
        assert(!!campaignId, 'CampaignRepository.updateCampaignPurchaseInputData: campaignId is required');

        const campaings = await this.getCampaigns({
            id: campaignId,
        });

        if (!campaings || campaings.length === 0) {
            throw new Error('CampaignRepository.updateCampaignStatus: failed to update campaign status, no campaign found.');
        }

        const oldPurchaseInputData = JSON.parse(JSON.stringify(campaings[0].purchase.input_data));

        const newPurchase = JSON.parse(JSON.stringify(campaings[0].purchase));

        newPurchase.input_data = {
            ...oldPurchaseInputData,
            ...input,
        };

        const result = await wrapSupabase(
            this.client
                .from('campaigns')
                .update({ purchase: newPurchase })
                .eq('id', campaignId)
                .select<'*', CampaignData>()
                .single<CampaignData>(),
        );

        if (!result) {
            throw new Error('CampaignRepository.updateCampaignPurchase: failed to update campaign purchase data, no result returned.');
        }

        return result;
    }

    public async updateCampaignMainInfo(campaignId: string, mainInfo: CampaignMainInfoUpdateData, userId?: string) {
        const { name, email, guaranteedStream } = mainInfo;

        assert(!!campaignId, 'CampaignRepository.updateCampaignMainInfo: campaignId is required');
        assert(!!name, 'CampaignRepository.updateCampaignMainInfo: mainInfo is required');
        assert(!!email, 'CampaignRepository.updateCampaignMainInfo: mainInfo is required');
        assert(!!guaranteedStream, 'CampaignRepository.updateCampaignMainInfo: mainInfo is required');

        const campaings = await this.getCampaigns({
            id: campaignId,
        });

        if (!campaings || campaings.length === 0) {
            throw new Error('CampaignRepository.updateCampaignStatus: failed to update campaign status, no campaign found.');
        }

        const newPurchase = JSON.parse(JSON.stringify(campaings[0].purchase)) as CampaignPurchase;

        if (newPurchase.input_data.username !== name) {
            newPurchase.input_data.username = name;
        }

        const newProduct = JSON.parse(JSON.stringify(campaings[0].product)) as ProductData;

        if (newProduct.meta.guaranteedStream !== guaranteedStream) {
            newProduct.meta.guaranteedStream = guaranteedStream;
        }

        let newUserId = campaings[0].user_id;

        if (campaings[0].email !== email) {
            newUserId = userId ? userId : null;
        }

        const result = await wrapSupabase(
            this.client
                .from('campaigns')
                .update({
                    user_id: newUserId,
                    email,
                    purchase: newPurchase,
                    product: newProduct,
                })
                .eq('id', campaignId)
                .select<'*', CampaignData>()
                .single<CampaignData>(),
        );

        if (!result) {
            throw new Error(
                'CampaignRepository.updateCampaignMainInfo: failed to update campaign main info, no result returned.',
            );
        }

        return result;
    }
}
